import {
  WebAuth,
  ChangePasswordOptions,
  Auth0Callback,
  CrossOriginLoginOptions,
  Auth0Error,
  DbSignUpOptions,
  AuthOptions,
  CatpchaConfiguration,
  Captcha
} from 'auth0-js';

import {
  Auth0Client,
  Auth0Config,
  AuthenticationSocialProvider
} from './auth0';

/**
 * Vistaprint Auth0 authentication client
 *
 * @export
 * @class VpAuth0Client
 * @implements {Auth0Client}
 */
export class VpAuth0Client implements Auth0Client {
  /**
   * Connection to Auth0
   *
   * @property
   * @private
   * @type {WebAuth}
   * @memberof VpAuth0Client
   */
  private auth0: WebAuth;

  /**
   * Auth0 configuration
   *
   * @property
   * @private
   * @type {Auth0Config}
   * @memberof VpAuth0Client
   */
  private config: Auth0Config;

  constructor(config: Auth0Config) {
    this.config = config;

    this.auth0 = new WebAuth({
      clientID: config.clientId,
      domain: config.domain,
      audience: config.audience,
      redirectUri: config.callbackUrl,
      responseType: config.responseType,
      leeway: config.leeway || 1,
      state: config.state,
      nonce: config.nonce,
      scope: config.scope,
      protocol: config.protocol,
      _csrf: config.csrf,
      overrides: {
        __tenant: config.tenant,
        __token_issuer: config.authorizationServerIssuer
      }
    } as AuthOptions);
  }

  login(
    email: string,
    password: string,
    callback: Auth0Callback<Auth0Error>,
    captcha?: string
  ): void {
    const options = {
      email,
      username: email,
      password,
      realm: this.config.connection,
      domain: this.config.domain,
      clientID: this.config.clientId,
      redirectUri: this.config.callbackUrl,
      responseType: this.config.responseType,
      scope: this.config.scope,
      audience: this.config.audience,
      brand: this.config.brand,
      protocol: this.config.protocol,
      wauth: this.config.wauth,
      _csrf: this.config.csrf,
      sso: false,
      captcha: captcha
    } as CrossOriginLoginOptions;

    this.auth0.login(options, callback);
  }

  signUp(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    exclusiveEmails: false,
    fewerEmails: false,
    signUpLocale: string,
    site: string,
    callback: Auth0Callback<Auth0Error>,
    captcha?: string
  ): void {
    const options = {
      email,
      password,
      connection: this.config.connection,
      scope: this.config.scope,
      captcha: captcha,
      user_metadata: {
        given_name: firstName,
        family_name: lastName,
        sign_up_for_exclusive_emails: exclusiveEmails.toString(),
        receive_fewer_emails: fewerEmails.toString(),
        site,
        sign_up_locale: signUpLocale
      }
    } as DbSignUpOptions;

    this.auth0.redirect.signupAndLogin(options, callback);
  }

  changePassword(email: string, callback: Auth0Callback<Auth0Error>): void {
    const options = {
      connection: this.config.connection,
      email
    } as ChangePasswordOptions;

    this.auth0.changePassword(options, callback);
  }

  renderCaptcha(element: HTMLElement, options?: CatpchaConfiguration, callback?: Auth0Callback<any>): Captcha {
    return this.auth0.renderCaptcha(element, options, callback)
  }

  authorizeSocial(
    provider: AuthenticationSocialProvider,
    loginHint?: string
  ): void {
    this.auth0.authorize({
      connection: this.config.connections[provider].connection,
      login_hint: loginHint
    });
  }
}
